import * as React from 'react';
import {
  Alert,
  Skeleton,
  Snackbar,
  useMediaQuery,
  useTheme,
  Box,
  Stack,
  Grid
} from '@mui/material';
import Masonry from '@mui/lab/Masonry';
// redux
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store/store';
import { setTasks } from '../../store/slices/tasksSlice';
import { setRecurringTasks } from '../../store/slices/recurringTaskSlice';
import { setEvents } from '../../store/slices/eventsSlice';
// axios
import api from '../../axios';
// types
import Task from '../../interfaces/Task.interface';
import RecurringTask from '../../interfaces/RecurringTask.interface';
import Event from '../../interfaces/Event.interface';
// components
import EnergyChart from '../../components/DashboardPage/EnergyChart';
import PriorityChart from '../../components/DashboardPage/PriorityChart';
import TimeChart from '../../components/DashboardPage/TimeChart';
import OpenItemsOverTime from '../../components/DashboardPage/OpenItemsOverTime';
import CompletionsHeatmap from '../../components/DashboardPage/CompletionsHeatmap';
import ItemsCount from '../../components/DashboardPage/ItemsCount';
import DueTodayGauge from '../../components/DashboardPage/DueTodayGauge';

export default function DashboardPage() {
  const dispatch = useDispatch<AppDispatch>();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const loadingHeights = [150, 30, 90, 70, 110, 150, 130, 80, 50, 90, 100, 150, 30, 50, 80];

  const [ showErrorBar, setShowErrorBar ] = React.useState<boolean>(false);
  
  const [ tasksReady, setTasksReady ] = React.useState<boolean>(false);
  const [ eventsReady, setEventsReady ] = React.useState<boolean>(false);
  const [ recurringTasksReady, setRecurringTasksReady ] = React.useState<boolean>(false);

  const fetchTasks = async () => {
    setTasksReady(false);

    api.get(`/tasks/normalized/get`)
      .then((res) => {
        console.log(res);
        const fetchedTasks: Task[] = res.data;

        dispatch(setTasks(fetchedTasks));
        setTasksReady(true);
      })
      .catch(console.log);
  };

  const fetchEvents = async () => {
    setEventsReady(false);

    api.get(`/events/get`)
      .then((res) => {
        console.log(res);
        const fetchedEvents: Event[] = res.data;

        dispatch(setEvents(fetchedEvents));
        setEventsReady(true);
      })
      .catch(console.log);
  };

  const fetchRecurringTasks = async () => {
    setRecurringTasksReady(false);

    api.get(`/recurring/get`)
      .then((res) => {
        console.log(res);
        const fetchedEvents: RecurringTask[] = res.data;

        dispatch(setRecurringTasks(fetchedEvents));
        setRecurringTasksReady(true);
      })
      .catch(console.log);
  };

  React.useEffect(() => {
    fetchTasks();
    fetchEvents();
    fetchRecurringTasks();
  }, []);

  return (
    <Box sx={{ width: '100%', minHeight: 393 }}>
      { isMobile ? (
        <React.Fragment>
          <DueTodayGauge />
          <OpenItemsOverTime />
          <EnergyChart />
          <PriorityChart />
          <TimeChart />
        </React.Fragment>
      ) : (
        !(tasksReady && eventsReady && recurringTasksReady) ? (
          <Masonry columns={3} spacing={2}>
            {loadingHeights.map((height, index) => (
              <Skeleton variant="rounded" key={index} sx={{ height }} />
            ))}
          </Masonry>
        ) : (
          <Stack spacing={2}>
            <Grid container spacing={2}>
              <Grid size={8}>
                <OpenItemsOverTime />
              </Grid>
              <Grid size={4}>
                <DueTodayGauge />
              </Grid>
            </Grid>

            <Box sx={{
              width: '100%'
            }}>
              <CompletionsHeatmap />
            </Box>

            <Grid container spacing={2}>
              <Grid size={4}>
                <EnergyChart />
              </Grid>
              <Grid size={4}>
                <TimeChart />
              </Grid>
              <Grid size={4}>
                <PriorityChart />
              </Grid>
            </Grid>

            <Box sx={{
              width: '100%',
            }}>
              <ItemsCount />
            </Box>
          </Stack>
        )
      )}
      
      <Snackbar
        open={showErrorBar}
        autoHideDuration={6000}
        onClose={() => { setShowErrorBar(false); }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
      >
        <Alert
          severity="error"
          variant="filled"
          sx={{ width: '100%' }}
        >
          There was an error fetching task data! Check your internet!
        </Alert>
      </Snackbar>
    </Box>
  );
}
